import * as React from "react";
import {
  apiClient,
  clearTokens,
  getAccessToken,
  onUnauthorized,
  setAccessToken,
} from "../lib/api-client";
import {
  clearRefreshToken,
  getRefreshToken,
  setRefreshToken,
} from "../lib/storage";

export interface Me {
  id: string;
  email: string;
  role: "user" | "admin" | "developer";
}

interface LoginChallengeResponse {
  pendingToken: string;
}

interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

interface AuthContextValue {
  user: Me | null;
  isLoading: boolean;
  login: (email: string) => Promise<{ pendingToken: string }>;
  verify: (pendingToken: string, code: string) => Promise<Me>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<Me | null>;
}

const Context = React.createContext<AuthContextValue | null>(null);

// 모듈 레벨 guard — React.StrictMode 가 effect 를 두 번 fire 시켜도
// /auth/me 부트스트랩은 페이지 로드당 한 번만.
let bootstrapped = false;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<Me | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  const refreshMe = React.useCallback(async (): Promise<Me | null> => {
    try {
      const me = await apiClient<Me>("/api/auth/me");
      setUser(me);
      return me;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  React.useEffect(() => {
    onUnauthorized(() => setUser(null));

    if (!bootstrapped) {
      bootstrapped = true;
      (async () => {
        const at = getAccessToken();
        const rt = getRefreshToken();
        if (!at && !rt) {
          setIsLoading(false);
          return;
        }
        // access token 있으면 /me 한 번에 끝. 만료됐으면 (401)
        // api-client 가 자동으로 refresh + 재시도 → /me 한 번 더만.
        const me = await refreshMe();
        if (!me) clearTokens();
        setIsLoading(false);
      })();
    }
    // StrictMode 가 effect 를 2회 fire 해도 위 async(1회차)가 isLoading 을
    // 정리한다. 여기서 setIsLoading(false) 를 호출하면 user 가 채워지기 전에
    // isLoading=false 가 되어, 직접 URL 진입 시 보호 라우트가 잘못 /login
    // (→ /leads) 으로 튕긴다.

    return () => {
      onUnauthorized(null);
    };
  }, [refreshMe]);

  const login = React.useCallback(async (email: string) => {
    return apiClient<LoginChallengeResponse>("/api/auth/login", {
      method: "POST",
      body: { email },
    });
  }, []);

  const verify = React.useCallback(
    async (pendingToken: string, code: string): Promise<Me> => {
      const tokens = await apiClient<TokenPair>("/api/auth/login/verify", {
        method: "POST",
        body: { pendingToken, code },
      });
      setAccessToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);
      const me = await apiClient<Me>("/api/auth/me");
      setUser(me);
      return me;
    },
    [],
  );

  const logout = React.useCallback(async (): Promise<void> => {
    const rt = getRefreshToken();
    if (rt) {
      try {
        await apiClient("/api/auth/logout", {
          method: "POST",
          body: { refreshToken: rt },
        });
      } catch {
        /* logout 실패해도 클라이언트는 토큰 정리 */
      }
    }
    clearTokens();
    setUser(null);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ user, isLoading, login, verify, logout, refreshMe }),
    [user, isLoading, login, verify, logout, refreshMe],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(Context);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
